'use client';
import { useTranslations } from 'next-intl';
import type React from 'react';
import { AgentLineGenerateImage } from '@/app/components/icons/AgentLineGenerateImage';
import { AgentLineGenerateVideo } from '@/app/components/icons/AgentLineGenerateVideo';
import { BaseLineQuickEdit } from '@/app/components/icons/BaseLineQuickEdit';
import { BaseLineS1 } from '@/app/components/icons/BaseLineS1';
import { LogoWhite } from '@/app/components/icons/LogoWhite';
import { ProductLineCreations } from '@/app/components/icons/ProductLineCreations';
import { ProductLineHome } from '@/app/components/icons/ProductLineHome';
import { ProductLineReference } from '@/app/components/icons/ProductLineReference';
import { ClientLink } from '@/app/components/shared/ClientLink';
import { FeatureTour } from '@/app/components/shared/FeatureTour';
import { HOME_PAGE, isEnterpriseDistribution, TOUR_KEYS } from '@/app/constants';
import { useStore } from '@/app/providers/store';
import { cn, isDistributionRouteVisible, reportEvent } from '@/app/utils';
import { usePathname } from '@/i18n/routing';
import { AgentLineClaw } from '../../icons/AgentLineClaw';
import { BaseLineProfile } from '../../icons/BaseLineProfile';
import { MenuItem } from './components/MenuItem';
import { SidebarUserToolbar } from './components/SidebarUserToolbar';
import type { SingleMenuItem } from './types';

const MENU_ITEM_ICON_CLASSNAME = 'size-5 text-system-white';
const SUB_MENU_ITEM_ICON_CLASSNAME = 'size-5 text-system-white64';

const PCSidebar: React.FC = () => {
  const t = useTranslations();
  const pathname = usePathname();
  // home 页侧边栏背景透明（让 hero 视频透出），其他页保持毛玻璃底色
  const isHomePage = pathname === HOME_PAGE || pathname.startsWith(`${HOME_PAGE}/`);
  const { isTeamSpace } = useStore((state) => ({
    isTeamSpace: state.isTeamSpace,
  }));
  const menuItems: SingleMenuItem[] = [
    {
      name: t('sidebar.home'),
      shortName: t('sidebar.shortItems.home'),
      icon: <ProductLineHome className={MENU_ITEM_ICON_CLASSNAME} />,
      path: HOME_PAGE,
      id: 'home',
      onClick: () => reportEvent('element_click', { element_name: 'menu - home' }),
    },
    ...(isTeamSpace
      ? [
          {
            name: t('sidebar.teamProfile'),
            shortName: t('sidebar.shortItems.teamProfile'),
            icon: <BaseLineProfile className={MENU_ITEM_ICON_CLASSNAME} />,
            path: '/team-profile',
            id: 'team-profile',
            onClick: () => reportEvent('element_click', { element_name: 'menu - team-profile' }),
          },
        ]
      : []),
    {
      name: t('sidebar.assets'),
      shortName: t('sidebar.shortItems.assets'),
      icon: <ProductLineCreations className={MENU_ITEM_ICON_CLASSNAME} />,
      path: '/assets/mycreations',
      activePaths: ['/assets/myreferences'],
      id: 'assets',
      onClick: () => reportEvent('element_click', { element_name: 'menu - assets' }),
    },
    {
      name: t('sidebar.creation'),
      shortName: t('sidebar.shortItems.creation'),
      icon: <ProductLineReference className={MENU_ITEM_ICON_CLASSNAME} />,
      path: '/create/character2video',
      activePaths: [
        '/create/character2video',
        '/create/img2video',
        '/create/text2video',
        '/create/reference2image',
        '/create/text2image',
      ],
      id: 'creation',
      onClick: () => reportEvent('element_click', { element_name: 'menu - creation' }),
      subMenuItems: [
        {
          name: t('sidebar.creationItems.video'),
          icon: <AgentLineGenerateVideo className={SUB_MENU_ITEM_ICON_CLASSNAME} />,
          path: '/create/character2video',
          id: 'creation-video',
          onClick: () => reportEvent('element_click', { element_name: 'menu - creation - video' }),
        },
        {
          name: t('sidebar.creationItems.image'),
          icon: <AgentLineGenerateImage className={SUB_MENU_ITEM_ICON_CLASSNAME} />,
          path: '/create/reference2image',
          id: 'creation-image',
          onClick: () => reportEvent('element_click', { element_name: 'menu - creation - image' }),
        },
      ],
    },
    {
      name: t('sidebar.s1'),
      shortName: t('sidebar.shortItems.s1'),
      icon: <BaseLineS1 className={MENU_ITEM_ICON_CLASSNAME} />,
      path: '/vidu-stream',
      id: 's1',
      onClick: () => reportEvent('element_click', { element_name: 'menu - s1' }),
    },
    ...(isTeamSpace
      ? []
      : [
          {
            name: t('sidebar.labItems.agent'),
            shortName: t('sidebar.shortItems.agent'),
            icon: <AgentLineClaw className={MENU_ITEM_ICON_CLASSNAME} />,
            path: '/claw',
            id: 'claw',
            onClick: () => {
              reportEvent('element_click', { element_name: 'menu - claw' });
              reportEvent('claw_page_view', { page_type: 'claw_page', enter_from: 'home_page' });
            },
          },
        ]),
    {
      name: t('sidebar.tools'),
      shortName: t('sidebar.shortItems.tools'),
      icon: <BaseLineQuickEdit className={MENU_ITEM_ICON_CLASSNAME} />,
      path: '/create/tools',
      id: 'tools',
      onClick: () => reportEvent('element_click', { element_name: 'menu - tools' }),
    },
  ];
  const visibleMenuItems = menuItems.filter((item) => isDistributionRouteVisible(item.path));

  return (
    <aside
      className={cn(
        'group relative flex h-full w-[105px] flex-col justify-between px-4 py-4 text-sm text-white backdrop-blur-[40px] md:hidden',
        isHomePage ? 'bg-transparent' : 'bg-[rgb(var(--system-bg08-rgb)/0.72)]',
      )}
      data-google-interstitial="false"
    >
      <div className="mt-0 mb-5 flex h-10 items-center justify-center">
        {!isEnterpriseDistribution && (
          <ClientLink href={HOME_PAGE} className="flex size-10 items-center justify-center">
            <LogoWhite className="text-3xl" />
          </ClientLink>
        )}
      </div>
      <nav className="hide-scrollbar min-h-0 flex-1 overflow-y-scroll">
        {visibleMenuItems.map((item) => (
          <div key={item.name} className="mx-auto mb-2 w-10">
            <MenuItem
              name={item.name}
              icon={item.icon}
              path={item.path}
              activePaths={item.activePaths}
              badge={item.badge}
              id={item.id}
              exact={item.exact}
              collapsed={true}
              shortName={item.shortName}
              subMenuItems={item.subMenuItems}
            />
          </div>
        ))}
      </nav>
      {!isTeamSpace && (
        <FeatureTour
          tourKey={TOUR_KEYS.CLAW_ENTRY_TOUR}
          tourConfig={{
            items: [
              {
                domId: 'claw',
                side: 'right',
                align: 'center',
                sideOffset: 12,
                description: [t('claw.tour.entry.description')],
                popoverCtxClassName: 'w-[280px]',
                popoverCtxInnerClassName: 'pr-7',
                highlightClassName: 'tour-highlight-soft',
              },
            ],
          }}
        />
      )}
      <SidebarUserToolbar />
    </aside>
  );
};

export default PCSidebar;
